// Rejestracja service workera, wyniesiona z `main.ts`, żeby punkt wejścia nie
// puchł o coś, co ma inny rytm życia niż render: ten moduł żyje na styku
// przeglądarki i cache'u, a `main.ts` ma tylko wywołać jedną funkcję i nie
// czekać na jej wynik.

export type StanPwa =
  | { rodzaj: 'brak' } // brak wsparcia albo rejestracja się nie powiodła (np. kontekst niebezpieczny)
  | { rodzaj: 'instaluje' }
  | { rodzaj: 'gotowe'; wersja: string } // komplet powłoki potwierdzony PRZEZ service workera, nie zgadnięty
  | { rodzaj: 'aktualizacja'; zastosuj: () => void };

/** Flaga MODUŁOWA, nie lokalna w funkcji: `controllerchange` potrafi odpalić
 * się więcej niż raz w życiu strony (np. druga aktualizacja później), a bez
 * jednego globalnego strażnika przeładowanie wchodzi w pętlę z samym sobą. */
let przeladowanoJuz = false;

export function zarejestrujWorkera(meldunek: (stan: StanPwa) => void): void {
  if (!('serviceWorker' in navigator)) {
    meldunek({ rodzaj: 'brak' });
    return;
  }

  // Po `load`, nie od razu: rejestracja i ewentualne pobranie workera nie
  // mają konkurować o pasmo i główny wątek z pierwszym renderem, a start
  // aplikacji ma działać z cache i nigdy nie czekać na sieć.
  //
  // Sprawdzenie `readyState` NIE jest tu ostrożnością i kosztowało cały tryb
  // bez sieci. Odkąd start aplikacji jest asynchroniczny (czeka na otwarcie
  // bazy i zasiew korpusu), ta funkcja bywa wołana JUŻ PO zdarzeniu `load`, a
  // nasłuch podpięty po fakcie nie odpala się nigdy. Service worker nie
  // rejestrował się wtedy w ogóle, aplikacja działała normalnie z sieci i
  // żaden wpis w konsoli o tym nie mówił: awaria widoczna wyłącznie po
  // odcięciu sieci albo po zajrzeniu do rejestracji workera.
  if (document.readyState === 'complete') {
    void obsluz(meldunek);
    return;
  }
  window.addEventListener('load', () => {
    void obsluz(meldunek);
  });
}

async function obsluz(meldunek: (stan: StanPwa) => void): Promise<void> {
  let rejestracja: ServiceWorkerRegistration;
  try {
    // `updateViaCache: 'none'`: sam `sw.js` ma się nigdy nie brać z cache'u
    // HTTP przeglądarki, bo o unieważnianiu jego treści decyduje już nazwa
    // pliku (build) i logika wewnątrz, podwójne cache'owanie tylko myli.
    rejestracja = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
  } catch {
    meldunek({ rodzaj: 'brak' });
    return;
  }

  meldunek({ rodzaj: 'instaluje' });
  sledzAktualizacje(rejestracja, meldunek);

  const aktywny = await czekajNaAktywnego(rejestracja);
  if (aktywny) {
    const stan = await zapytajOStan(aktywny);
    if (stan?.gotowe) meldunek({ rodzaj: 'gotowe', wersja: stan.wersja });
  }

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (przeladowanoJuz) return;
    przeladowanoJuz = true;
    location.reload();
  });

  // Powrót do karty jest jedynym momentem, w którym w ogóle warto spytać
  // sieć o nowszą wersję: wynik nie blokuje niczego i nie rzuca dalej, bo
  // brak łączności przy takim sprawdzeniu nie jest błędem aplikacji.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') rejestracja.update().catch(() => {});
  });
}

/** Melduje `aktualizacja`, gdy tylko pojawi się worker w stanie `waiting`,
 * zarówno zastany przy rejestracji, jak i znaleziony później przez `update()`. */
function sledzAktualizacje(rejestracja: ServiceWorkerRegistration, meldunek: (stan: StanPwa) => void): void {
  const zglos = (worker: ServiceWorker) => {
    meldunek({ rodzaj: 'aktualizacja', zastosuj: () => worker.postMessage({ typ: 'przelacz' }) });
  };

  if (rejestracja.waiting) zglos(rejestracja.waiting);

  rejestracja.addEventListener('updatefound', () => {
    const nowy = rejestracja.installing;
    if (!nowy) return;
    nowy.addEventListener('statechange', () => {
      // `installed` przy istniejącym kontrolerze = jest już KOGO podmieniać,
      // czyli to aktualizacja, a nie pierwsza instalacja tej karty.
      if (nowy.state === 'installed' && navigator.serviceWorker.controller) zglos(nowy);
    });
  });
}

function czekajNaAktywnego(rejestracja: ServiceWorkerRegistration): Promise<ServiceWorker | null> {
  if (rejestracja.active) return Promise.resolve(rejestracja.active);

  const worker = rejestracja.installing ?? rejestracja.waiting;
  if (!worker) return Promise.resolve(null);

  return new Promise((resolve) => {
    const nasluch = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', nasluch);
        resolve(worker);
      } else if (worker.state === 'redundant') {
        // Instalacja padła (np. `addAll` odrzucił): nie ma kogo pytać o stan,
        // stara wersja (jeśli jest) zostaje bez zmian i bez meldunku 'gotowe'.
        worker.removeEventListener('statechange', nasluch);
        resolve(null);
      }
    };
    worker.addEventListener('statechange', nasluch);
  });
}

function zapytajOStan(worker: ServiceWorker): Promise<{ wersja: string; gotowe: boolean } | null> {
  return new Promise((resolve) => {
    const kanal = new MessageChannel();
    kanal.port1.onmessage = (zdarzenie) => resolve(zdarzenie.data as { wersja: string; gotowe: boolean });
    try {
      worker.postMessage({ typ: 'stan' }, [kanal.port2]);
    } catch {
      resolve(null);
    }
  });
}
